import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';
import { GripVertical, Plus, RotateCcw, Trash2, RefreshCw } from 'lucide-react';
import {
    addGuideQuestion,
    addGuideSection,
    deleteGuideSection,
    patchGuideQuestion,
    patchGuideSection,
    regenGuideQuestion,
} from '../../services/api/collectDataService';
import { GenericModal } from '../modal';

export type GuideQuestionRow = {
    id: string;
    text: string;
    order?: number;
    status?: string;
    ai_generated?: boolean;
};

export type GuideSubSectionRow = {
    id: string;
    name?: string;
    objective?: string;
    order?: number;
    colour?: string | null;
    questions?: GuideQuestionRow[];
};

export type GuideRoleSectionRow = {
    id: string;
    role_label?: string;
    order?: number;
    sections?: GuideSubSectionRow[];
};

export type GuideDoc = {
    role_sections?: GuideRoleSectionRow[];
    sections?: GuideSubSectionRow[];
} | null;

type Props = {
    collectionId: string | undefined;
    accessToken: string | undefined;
    guide: GuideDoc;
    canEdit: boolean;
    onGuideUpdated: (g: GuideDoc) => void;
    onError: (message: string) => void;
};

function sortRoleSections(g: GuideDoc): GuideRoleSectionRow[] {
    const roles = g?.role_sections?.length
        ? [...g.role_sections]
        : g?.sections?.length
          ? [{ id: 'legacy', role_label: 'General', order: 0, sections: [...g.sections] }]
          : [];
    roles.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const r of roles) {
        const secs = r.sections ? [...r.sections] : [];
        secs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        r.sections = secs;
    }
    return roles;
}

function sortQuestions(sec: GuideSubSectionRow) {
    const qs = sec.questions ? [...sec.questions] : [];
    qs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return qs;
}

function totalSubSectionCount(roles: GuideRoleSectionRow[]): number {
    return roles.reduce((n, r) => n + (r.sections?.length ?? 0), 0);
}

const CollectGuideReviewPanel: React.FC<Props> = ({
    collectionId,
    accessToken,
    guide,
    canEdit,
    onGuideUpdated,
    onError,
}) => {
    const { t } = useTranslation();
    const [busyQ, setBusyQ] = useState<string | null>(null);
    const [busySec, setBusySec] = useState<string | null>(null);
    const [busyRoleSec, setBusyRoleSec] = useState<string | null>(null);
    const [busyReorder, setBusyReorder] = useState(false);
    const [newDraftBySection, setNewDraftBySection] = useState<Record<string, string>>({});
    const [dragId, setDragId] = useState<string | null>(null);
    const [deleteSectionId, setDeleteSectionId] = useState<string | null>(null);

    const roleSections = useMemo(() => sortRoleSections(guide), [guide]);
    const subSectionTotal = useMemo(() => totalSubSectionCount(roleSections), [roleSections]);

    const applyRes = useCallback(
        (res: { status?: number; body?: { data?: GuideDoc; description?: string } }) => {
            if (res.status === 200 && res.body?.data) {
                onGuideUpdated(res.body.data);
                return true;
            }
            onError(res.body?.description || t('form.collectWorkspace.guideSaveFailed'));
            return false;
        },
        [onGuideUpdated, onError, t],
    );

    const onPatchQuestion = async (
        questionId: string,
        payload: { text?: string; order?: number; status?: string },
    ) => {
        if (!collectionId || !accessToken) return;
        setBusyQ(questionId);
        try {
            const res = await patchGuideQuestion(collectionId, questionId, payload, accessToken);
            applyRes(res);
        } finally {
            setBusyQ(null);
        }
    };

    const onPatchSection = async (
        sectionId: string,
        payload: { name?: string; objective?: string; order?: number },
    ) => {
        if (!collectionId || !accessToken) return;
        setBusySec(sectionId);
        try {
            const res = await patchGuideSection(collectionId, sectionId, payload, accessToken);
            applyRes(res);
        } finally {
            setBusySec(null);
        }
    };

    const onRegenQuestion = async (questionId: string) => {
        if (!collectionId || !accessToken) return;
        setBusyQ(questionId);
        try {
            const res = await regenGuideQuestion(collectionId, questionId, accessToken);
            applyRes(res);
        } finally {
            setBusyQ(null);
        }
    };

    const executeDeleteSection = async (): Promise<boolean> => {
        if (!collectionId || !accessToken || !deleteSectionId) return false;
        if (subSectionTotal <= 1) return false;
        setBusySec(deleteSectionId);
        try {
            const res = await deleteGuideSection(collectionId, deleteSectionId, accessToken);
            return applyRes(res);
        } finally {
            setBusySec(null);
        }
    };

    const onAddQuestion = async (sectionId: string) => {
        const text = (newDraftBySection[sectionId] || '').trim();
        if (!collectionId || !accessToken || !text) return;
        setBusySec(sectionId);
        try {
            const res = await addGuideQuestion(collectionId, sectionId, text, accessToken);
            if (applyRes(res)) {
                setNewDraftBySection((prev) => ({ ...prev, [sectionId]: '' }));
            }
        } finally {
            setBusySec(null);
        }
    };

    const onAddSection = async (roleSectionId: string) => {
        if (!collectionId || !accessToken || roleSectionId === 'legacy') return;
        setBusyRoleSec(roleSectionId);
        try {
            const res = await addGuideSection(
                collectionId,
                roleSectionId,
                {
                    name: t('form.collectWorkspace.guideNewSectionDefaultName', {
                        defaultValue: 'New section',
                    }),
                    objective: '',
                },
                accessToken,
            );
            applyRes(res);
        } finally {
            setBusyRoleSec(null);
        }
    };

    const onDragStart = (e: React.DragEvent, sectionId: string) => {
        if (!canEdit) return;
        setDragId(sectionId);
        e.dataTransfer.setData('text/plain', sectionId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const onDropOnSection = async (e: React.DragEvent, targetId: string, roleBlock: GuideRoleSectionRow) => {
        e.preventDefault();
        const fromId = e.dataTransfer.getData('text/plain') || dragId;
        setDragId(null);
        if (!fromId || fromId === targetId || !collectionId || !accessToken) return;

        const ordered = roleBlock.sections ? [...roleBlock.sections] : [];
        const fromIdx = ordered.findIndex((s) => s.id === fromId);
        const toIdx = ordered.findIndex((s) => s.id === targetId);
        if (fromIdx < 0 || toIdx < 0) return;

        const next = [...ordered];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);

        setBusyReorder(true);
        try {
            let lastRes: { status?: number; body?: { data?: GuideDoc; description?: string } } | null = null;
            for (let i = 0; i < next.length; i++) {
                const sec = next[i];
                lastRes = await patchGuideSection(collectionId, sec.id, { order: i }, accessToken);
                if (lastRes.status !== 200) {
                    applyRes(lastRes);
                    return;
                }
            }
            if (lastRes) applyRes(lastRes);
        } finally {
            setBusyReorder(false);
        }
    };

    if (!roleSections.length) {
        return (
            <p className="collect-muted collect-guide-empty">{t('form.collectWorkspace.guideEmpty')}</p>
        );
    }

    return (
        <div className={`collect-guide-review ${busyReorder ? 'is-busy' : ''}`}>
            {roleSections.map((roleBlock) => (
                <div key={roleBlock.id} className="collect-guide-role-block">
                    <header className="collect-guide-role-head">
                        <h3 className="collect-guide-role-title">
                            {roleBlock.role_label ||
                                t('form.collectWorkspace.guideRoleFallback', { defaultValue: 'Role' })}
                        </h3>
                    </header>

                    {(roleBlock.sections ?? []).map((sec) => {
                        const qs = sortQuestions(sec);
                        const keptCount = qs.filter((q) => q.status === 'kept').length;
                        const newDraft = newDraftBySection[sec.id] ?? '';
                        return (
                            <section
                                key={sec.id}
                                className="collect-guide-section"
                                onDragOver={onDragOver}
                                onDrop={(e) => void onDropOnSection(e, sec.id, roleBlock)}
                            >
                                <div className="collect-guide-section-head">
                                    <div className="collect-guide-section-head-inner">
                                        {canEdit && (
                                            <div
                                                className="collect-guide-drag"
                                                draggable
                                                role="button"
                                                tabIndex={0}
                                                onDragStart={(e) => onDragStart(e, sec.id)}
                                                aria-label={t('form.collectWorkspace.guideDragHint')}
                                                title={t('form.collectWorkspace.guideDragHint')}
                                            >
                                                <GripVertical size={18} strokeWidth={1.75} />
                                            </div>
                                        )}
                                        <div className="collect-guide-section-fields">
                                            <label className="collect-guide-label">
                                                {t('form.collectWorkspace.guideSectionName')}
                                            </label>
                                            <input
                                                type="text"
                                                className="field-input collect-guide-title-input"
                                                defaultValue={sec.name || ''}
                                                disabled={!canEdit || busySec === sec.id || busyReorder}
                                                key={`name-${sec.id}-${sec.name}`}
                                                onBlur={(e) => {
                                                    const v = e.target.value.trim();
                                                    if (v !== (sec.name || '').trim()) void onPatchSection(sec.id, { name: v });
                                                }}
                                            />
                                            <label className="collect-guide-label">
                                                {t('form.collectWorkspace.guideSectionObjective')}
                                            </label>
                                            <TextareaAutosize
                                                className="field-input collect-guide-objective"
                                                minRows={2}
                                                defaultValue={sec.objective || ''}
                                                disabled={!canEdit || busySec === sec.id || busyReorder}
                                                key={`obj-${sec.id}-${sec.objective}`}
                                                onBlur={(e) => {
                                                    const v = e.target.value.trim();
                                                    if (v !== (sec.objective || '').trim())
                                                        void onPatchSection(sec.id, { objective: v });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {canEdit && subSectionTotal > 1 ? (
                                        <button
                                            type="button"
                                            className="collect-guide-section-delete"
                                            title={t('form.collectWorkspace.deleteSection', 'Delete section')}
                                            aria-label={t('form.collectWorkspace.deleteSection', 'Delete section')}
                                            disabled={busySec === sec.id || busyReorder}
                                            onClick={() => setDeleteSectionId(sec.id)}
                                        >
                                            <Trash2 size={18} strokeWidth={1.75} />
                                        </button>
                                    ) : null}
                                </div>

                                <ul className="collect-guide-questions">
                                    {qs.map((q) => {
                                        const removed = q.status === 'removed';
                                        return (
                                            <li
                                                key={q.id}
                                                className={`collect-guide-q ${removed ? 'is-removed' : ''}`}
                                            >
                                                <TextareaAutosize
                                                    key={`${q.id}-${q.text}`}
                                                    className="field-input collect-guide-q-text"
                                                    minRows={2}
                                                    defaultValue={q.text}
                                                    disabled={!canEdit || busyQ === q.id || removed || busyReorder}
                                                    readOnly={removed}
                                                    onBlur={(e) => {
                                                        if (removed) return;
                                                        const v = e.target.value.trim();
                                                        if (!v) {
                                                            e.target.value = q.text;
                                                            return;
                                                        }
                                                        if (v !== q.text.trim()) void onPatchQuestion(q.id, { text: v });
                                                    }}
                                                />
                                                {canEdit && (
                                                    <div className="collect-guide-q-actions">
                                                        {removed ? (
                                                            <button
                                                                type="button"
                                                                className="collect-guide-icon-btn"
                                                                title={t('form.collectWorkspace.guideRestore')}
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => void onPatchQuestion(q.id, { status: 'kept' })}
                                                                disabled={busyQ === q.id || busyReorder}
                                                            >
                                                                <RotateCcw size={18} strokeWidth={1.75} />
                                                                <span>{t('form.collectWorkspace.guideRestore')}</span>
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="collect-guide-icon-btn danger"
                                                                    title={t('form.collectWorkspace.guideHide')}
                                                                    aria-label={t('form.collectWorkspace.guideHide')}
                                                                    onMouseDown={(e) => e.preventDefault()}
                                                                    onClick={() =>
                                                                        void onPatchQuestion(q.id, { status: 'removed' })
                                                                    }
                                                                    disabled={busyQ === q.id || busyReorder}
                                                                >
                                                                    <Trash2 size={18} strokeWidth={1.75} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="collect-guide-icon-btn"
                                                                    title={t(
                                                                        'form.collectWorkspace.guideRegenShort',
                                                                        'Regenerate wording',
                                                                    )}
                                                                    aria-label={t(
                                                                        'form.collectWorkspace.guideRegenShort',
                                                                        'Regenerate wording',
                                                                    )}
                                                                    onMouseDown={(e) => e.preventDefault()}
                                                                    onClick={() => void onRegenQuestion(q.id)}
                                                                    disabled={busyQ === q.id || busyReorder}
                                                                >
                                                                    <RefreshCw size={18} strokeWidth={1.75} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                {removed && (
                                                    <span className="collect-guide-removed-badge">
                                                        {t('form.collectWorkspace.guideQuestionRemoved')}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>

                                {canEdit && (
                                    <div className="collect-guide-add">
                                        {keptCount >= 5 ? (
                                            <p className="collect-muted collect-guide-max">
                                                {t('form.collectWorkspace.guideMaxQuestions')}
                                            </p>
                                        ) : (
                                            <>
                                                <TextareaAutosize
                                                    className="field-input"
                                                    minRows={2}
                                                    placeholder={t('form.collectWorkspace.guideAddPlaceholder')}
                                                    value={newDraft}
                                                    disabled={busyReorder}
                                                    onChange={(e) =>
                                                        setNewDraftBySection((prev) => ({
                                                            ...prev,
                                                            [sec.id]: e.target.value,
                                                        }))
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    className="action-btn primary collect-guide-add-btn"
                                                    disabled={!newDraft.trim() || busySec === sec.id || busyReorder}
                                                    onClick={() => void onAddQuestion(sec.id)}
                                                >
                                                    <Plus size={16} strokeWidth={2} />
                                                    {t('form.collectWorkspace.guideAddQuestion')}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                    {canEdit ? (
                        <div className="collect-guide-add-section">
                            {(roleBlock.sections?.length ?? 0) >= 5 ? (
                                <p className="collect-muted collect-guide-max">
                                    {t('form.collectWorkspace.guideMaxSections', {
                                        defaultValue: 'Maximum 5 sections per role.',
                                    })}
                                </p>
                            ) : (
                                <button
                                    type="button"
                                    className="action-btn collect-guide-add-section-btn"
                                    disabled={
                                        busyReorder ||
                                        busyRoleSec === roleBlock.id ||
                                        roleBlock.id === 'legacy'
                                    }
                                    onClick={() => void onAddSection(roleBlock.id)}
                                >
                                    <Plus size={16} strokeWidth={2} />
                                    {busyRoleSec === roleBlock.id
                                        ? t('words.loading', 'Loading…')
                                        : t('form.collectWorkspace.guideAddSection', {
                                              defaultValue: 'Add section',
                                          })}
                                </button>
                            )}
                        </div>
                    ) : null}
                </div>
            ))}

            {deleteSectionId ? (
                <GenericModal
                    title={t('form.collectWorkspace.deleteSection', { defaultValue: 'Delete section' })}
                    actionButton={t('words.delete')}
                    sentence={t('form.collectWorkspace.deleteSectionConfirm', {
                        defaultValue: 'Remove this entire section?',
                    })}
                    processId={deleteSectionId}
                    modalDomId={`collect-guide-delete-section-${deleteSectionId}`}
                    autoShow
                    onHidden={() => setDeleteSectionId(null)}
                    action={async () => {
                        const ok = await executeDeleteSection();
                        if (ok) setDeleteSectionId(null);
                        return ok;
                    }}
                />
            ) : null}
        </div>
    );
};

export default CollectGuideReviewPanel;
